import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import {
  PlusIcon,
  PowerIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import AdelantosService from "../../services/adelantosService";
import GruposAfinidadService from "../../services/gruposAfinidadService";
import { sortReglas } from "./utils";
import ReglaFormModal from "./ReglaFormModal";

/**
 * AdelantoEfectivoManager
 *
 * Componente principal para la gestión de reglas de adelantos en efectivo.
 * Gestiona el estado completo del módulo, renderiza la tabla y coordina
 * las acciones de creación, edición, toggle y eliminación.
 *
 * Requirements: 1.1, 1.4, 1.5, 1.6
 */
export default function AdelantoEfectivoManager() {
  // ── Estado ──────────────────────────────────────────────────────────────
  /** Lista de reglas cargadas desde el backend */
  const [reglas, setReglas] = useState([]);
  /** Spinner de carga inicial */
  const [loading, setLoading] = useState(false);
  /** Mensaje de error de carga, o null si no hay error */
  const [error, setError] = useState(null);
  /** ID de la regla cuyo toggle está en vuelo (null = ninguno) */
  const [pendingToggleId, setPendingToggleId] = useState(null);
  /** ID de la regla cuya eliminación está en vuelo (null = ninguno) */
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  /** null = modal cerrado, {} = modo crear, Regla = modo editar */
  const [modalTarget, setModalTarget] = useState(null);

  // ── Carga de datos ───────────────────────────────────────────────────────
  /**
   * Obtiene todas las reglas mediante GET /jwt/adelantos/reglas.
   * Muestra spinner mientras carga y mensaje de error con botón "Reintentar" si falla.
   * Requirement: 1.1, 1.4, 1.5
   */
  const loadReglas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Cargamos reglas y grupos en paralelo para enriquecer nombre_grupo
      const [resReglas, resGrupos] = await Promise.all([
        AdelantosService.getReglas(),
        GruposAfinidadService.getAll().catch(() => []), // no bloquear si falla
      ]);

      // Mapa id → nombre (el servicio ya devuelve [{ id, nombre }])
      const gruposMap = {};
      for (const g of resGrupos) {
        if (g.nombre) gruposMap[g.id] = g.nombre;
      }

      // Extraer el array de reglas
      // Estructura: { status, success, message, data: [...grupos] }
      const raw =
        resReglas?.data?.data ??
        resReglas?.data ??
        resReglas?.result ??
        resReglas ??
        [];
      const list = Array.isArray(raw) ? raw : [];

      // Aplanar: cada grupo tiene N opciones → una fila por opción
      const normalized = [];
      for (const grupo of list) {
        const idGrupo = grupo.idGrupoAfinidad ?? grupo.id_grupo_afinidad;
        const nombreGrupo =
          grupo.nombreGrupo ??
          grupo.nombre_grupo ??
          gruposMap[idGrupo] ??
          `Grupo ${idGrupo ?? ""}`;

        const opciones = Array.isArray(grupo.opciones)
          ? grupo.opciones
          : [grupo];

        for (const opcion of opciones) {
          // plan viene como { id, nombre, codigoPos, cuotas: [1,3,6] } o null
          const plan = opcion.plan ?? null;
          const cuotasDisplay = Array.isArray(plan?.cuotas)
            ? plan.cuotas.map((c) =>
                typeof c === "object" ? (c.cuota ?? c.id) : c,
              )
            : [];

          normalized.push({
            id: opcion.id ?? grupo.id,
            id_grupo_afinidad: idGrupo,
            nombre_grupo: nombreGrupo,
            score_min: grupo.scoreMin ?? grupo.score_min ?? null,
            score_max: grupo.scoreMax ?? grupo.score_max ?? null,
            monto: opcion.monto ?? 0,
            // plan completo guardado para edición
            id_plan: plan?.id ?? null,
            nombre_plan: plan?.nombre ?? "—",
            cuotas: cuotasDisplay,
            activo: opcion.activo === 1 || opcion.activo === true,
          });
        }
      }

      setReglas(normalized);
    } catch {
      setError("No se pudieron cargar las reglas de adelantos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReglas();
  }, [loadReglas]);

  // ── Acciones del modal ───────────────────────────────────────────────────
  /** Abre el modal en modo crear */
  const handleNuevaRegla = () => setModalTarget({});

  /** Abre el modal en modo editar con la regla seleccionada */
  const handleEditarRegla = (regla) => setModalTarget(regla);

  /** Cierra el modal y recarga la lista tras guardar */
  const handleModalSaved = () => {
    setModalTarget(null);
    loadReglas();
  };

  // ── Toggle activo/inactivo ────────────────────────────────────────────────
  /**
   * Muestra confirmación SweetAlert2 y ejecuta PATCH toggle.
   * Actualiza el badge localmente sin recargar la lista.
   * Requirements: 4.2, 4.3, 4.4, 4.5, 4.6, 4.7
   */
  const handleToggle = async (regla) => {
    const activar = !regla.activo;
    const { isConfirmed } = await Swal.fire({
      title: activar ? "¿Activar regla?" : "¿Desactivar regla?",
      text: `Grupo: ${regla.nombre_grupo}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: activar ? "Activar" : "Desactivar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: activar ? "#56C401" : "#e53e3e",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
    });
    if (!isConfirmed) return;
    setPendingToggleId(regla.id);
    try {
      await AdelantosService.toggleRegla(regla.id);
      setReglas((prev) =>
        prev.map((r) => (r.id === regla.id ? { ...r, activo: activar } : r)),
      );
    } catch {
      Swal.fire("Error", "No se pudo cambiar el estado de la regla.", "error");
    } finally {
      setPendingToggleId(null);
    }
  };

  // ── Eliminación ──────────────────────────────────────────────────────────
  /**
   * Muestra confirmación SweetAlert2 y ejecuta DELETE.
   * Elimina la fila localmente tras respuesta exitosa.
   * Requirements: 5.2, 5.3, 5.4, 5.5, 5.6
   */
  const handleEliminar = async (regla) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar regla?",
      text: "Esta acción es permanente e irreversible.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#e53e3e",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
    });
    if (!isConfirmed) return;
    setPendingDeleteId(regla.id);
    try {
      await AdelantosService.deleteRegla(regla.id);
      setReglas((prev) => prev.filter((r) => r.id !== regla.id));
      Swal.fire({
        icon: "success",
        title: "Regla eliminada",
        position: "top-end",
        toast: true,
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo eliminar la regla.", "error");
    } finally {
      setPendingDeleteId(null);
    }
  };

  // ── Reglas ordenadas para renderizar ────────────────────────────────────
  const sortedReglas = sortReglas(reglas);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Parámetros de Adelantos en Efectivo
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestión de reglas de condiciones para adelantos en efectivo.
          </p>
        </div>
        <button
          onClick={handleNuevaRegla}
          className="flex items-center gap-2 px-5 py-2 rounded-xl bg-custom-green-ccard-1 text-white text-sm font-medium hover:bg-custom-green-ccard-2 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Nueva regla
        </button>
      </div>

      {/* Tabla / estados */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-black/5 overflow-hidden">
        {/* Estado: cargando — Requirement 1.4 */}
        {loading && (
          <div className="flex items-center justify-center h-48 gap-3 text-gray-400">
            <div className="h-7 w-7 rounded-full border-4 border-custom-green-ccard-1 border-t-transparent animate-spin" />
            <span className="text-sm">Cargando reglas...</span>
          </div>
        )}

        {/* Estado: error — Requirement 1.5 */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <p className="text-red-500 text-sm">{error}</p>
            <button
              onClick={loadReglas}
              className="text-sm text-custom-green-ccard-1 underline hover:no-underline"
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Estado: lista vacía — Requirement 1.6 */}
        {!loading && !error && sortedReglas.length === 0 && (
          <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-400">
            <p className="text-sm">No existen reglas configuradas.</p>
            <button
              onClick={handleNuevaRegla}
              className="text-sm text-custom-green-ccard-1 underline hover:no-underline"
            >
              Crear la primera
            </button>
          </div>
        )}

        {/* Tabla de reglas — se completa en tarea 3.2 */}
        {!loading && !error && sortedReglas.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Grupo Afinidad
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Score Mín
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Score Máx
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Monto
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Plan / Cuotas
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Estado
                  </th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sortedReglas.map((regla) => (
                  <tr
                    key={regla.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-5 py-3 font-medium text-gray-800">
                      {regla.nombre_grupo}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {regla.score_min !== null && regla.score_min !== undefined
                        ? regla.score_min
                        : "Sin filtro"}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {regla.score_max !== null && regla.score_max !== undefined
                        ? regla.score_max
                        : "Sin filtro"}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      ${Number(regla.monto).toFixed(2)}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      <div className="text-xs font-medium text-gray-700">
                        {regla.nombre_plan}
                      </div>
                      {regla.cuotas.length > 0 && (
                        <div className="text-xs text-gray-400">
                          {regla.cuotas.join(", ")} cuotas
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {regla.activo ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                          Inactivo
                        </span>
                      )}
                    </td>
                    {/* Columna de acciones */}
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Toggle activo/inactivo */}
                        <button
                          onClick={() => handleToggle(regla)}
                          disabled={pendingToggleId === regla.id}
                          title={regla.activo ? "Desactivar" : "Activar"}
                          className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            regla.activo
                              ? "text-green-600 hover:bg-green-50"
                              : "text-gray-400 hover:bg-gray-100"
                          }`}
                        >
                          <PowerIcon className="h-4 w-4" />
                        </button>
                        {/* Editar */}
                        <button
                          onClick={() => handleEditarRegla(regla)}
                          title="Editar"
                          className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        {/* Eliminar */}
                        <button
                          onClick={() => handleEliminar(regla)}
                          disabled={pendingDeleteId === regla.id}
                          title="Eliminar"
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contador de reglas */}
      {!loading && !error && (
        <p className="text-xs text-gray-400 text-right">
          {sortedReglas.length} regla{sortedReglas.length !== 1 ? "s" : ""}{" "}
          configurada{sortedReglas.length !== 1 ? "s" : ""}
        </p>
      )}

      {/* Modal — Requirements: 1.1, 2.1, 3.1, 5.1 */}
      {modalTarget !== null && (
        <ReglaFormModal
          regla={Object.keys(modalTarget).length > 0 ? modalTarget : null}
          onClose={() => setModalTarget(null)}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
}
